'use client';
import React, { useState } from 'react';
import { DEFAULT_FACTORS, calculateLighting, type RoomType, type LightingCalculationResult } from '@/lighting-app/data/lightingStandards';
import WizardProgress from './WizardProgress';
import StepRoomType from './StepRoomType';
import StepDimensions from './StepDimensions';
import StepFixture from './StepFixture';
import StepResults from './StepResults';

export interface DesignWizardState {
  step: number;
  roomType: RoomType | null;
  length: string;
  width: string;
  ceilingHeight: string;
  workingHeight: string;
  fixtureId: string;
  fixtureLumens: string;
  fixtureWatts: string;
  beamAngle: string;
  colorTemp: string;
  useAdvanced: boolean;
  uf: string;
  mf: string;
  result: LightingCalculationResult | null;
}

const INITIAL_STATE: DesignWizardState = {
  step: 1,
  roomType: null,
  length: '',
  width: '',
  ceilingHeight: '',
  workingHeight: '0.85',
  fixtureId: '',
  fixtureLumens: '',
  fixtureWatts: '',
  beamAngle: '120',
  colorTemp: '4000',
  useAdvanced: false,
  uf: String(DEFAULT_FACTORS.uf),
  mf: String(DEFAULT_FACTORS.mf),
  result: null,
};

export default function DesignWizardClient() {
  const [state, setState] = useState<DesignWizardState>(INITIAL_STATE);

  const updateState = (updates: Partial<DesignWizardState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  };

  const goToStep = (step: number) => updateState({ step });

  const handleCalculate = () => {
    if (!state.roomType) return;
    const lumens = parseFloat(state.fixtureLumens);
    const watts = parseFloat(state.fixtureWatts);
    if (!lumens || !watts) return;

    const result = calculateLighting({
      roomType: state.roomType,
      length: parseFloat(state.length),
      width: parseFloat(state.width),
      ceilingHeight: parseFloat(state.ceilingHeight) || 2.8,
      workingHeight: parseFloat(state.workingHeight) || 0.85,
      fixtureLumens: lumens,
      fixtureWatts: watts,
      beamAngle: parseFloat(state.beamAngle) || 120,
      uf: parseFloat(state.uf),
      mf: parseFloat(state.mf),
      useAdvanced: state.useAdvanced,
    });

    updateState({ result, step: 4 });
  };

  const handleReset = () => setState(INITIAL_STATE);

  return (
    <div className="max-w-screen-lg mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <WizardProgress currentStep={state.step} totalSteps={4} />

      <div className="fade-in">
        {state.step === 1 && (
          <StepRoomType
            selected={state.roomType}
            onSelect={(room) => updateState({ roomType: room, step: 2 })}
          />
        )}
        {state.step === 2 && (
          <StepDimensions
            state={state}
            onChange={updateState}
            onBack={() => goToStep(1)}
            onNext={() => goToStep(3)}
          />
        )}
        {state.step === 3 && (
          <StepFixture
            state={state}
            onChange={updateState}
            onBack={() => goToStep(2)}
            onCalculate={handleCalculate}
          />
        )}
        {state.step === 4 && state.result && (
          <StepResults
            state={state}
            result={state.result}
            onBack={() => goToStep(3)}
            onReset={handleReset}
          />
        )}
      </div>
    </div>
  );
}